import { TranslationProvider } from '@shared/translation';
import { mergeTexts, withTextKeys } from '@shared/translation/textTree';
import { useQueryClient } from '@tanstack/react-query';
import { setDefaultOptions } from 'date-fns';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Skeleton } from '@/component/Skeleton';
import { attachToDebug } from '@/util/debug';
import { languages } from './config';
import type { AllTexts } from './types';
import { useShowKeys } from './useShowKeys';
import { useStoredLanguage } from './useStoredLanguage';
import { getFallbackTranslations } from './util';
import { handleTranslationWarning } from './warnings';

const devTextsPromise = import('../../text.js');

export interface LangContextProps {
  language: string;
  changeLanguage: (language: string) => void;
  showKeys: boolean;
  setShowKeys: (showKeys: boolean) => void;
  isReady: boolean;
}

const LangContext = createContext<LangContextProps | undefined>(undefined);

interface LangProviderProps {
  defaultLanguage: string;
  children: ReactNode;
}

const createPromise = () => Promise.resolve(undefined);

const LangProvider = ({ children, defaultLanguage }: LangProviderProps) => {
  const { language, updateLanguage, isReady } =
    useStoredLanguage(defaultLanguage);
  const [texts, setTexts] = useState<AllTexts | undefined>();
  const [showKeys, setShowKeys] = useShowKeys();
  const queryClient = useQueryClient();

  attachToDebug('lang', { language, changeLanguage: updateLanguage, texts });

  useEffect(() => {
    const config = languages.find((lang) => lang.id === language);
    const textsPromise = config?.texts() ?? createPromise();

    Promise.all([devTextsPromise, textsPromise])
      .then(([devTranslations, translations]) => {
        const fallbackTranslations = getFallbackTranslations(devTranslations);
        const newTexts = createTexts(
          { showKeys },
          fallbackTranslations,
          translations
        );
        setTexts(newTexts);
      })
      .catch((error) => {
        console.error('Failed to load translations', error);
      });
  }, [language, showKeys]);

  useEffect(() => {
    const config = languages.find((lang) => lang.id === language);
    const promise = config?.dateLocale() ?? createPromise();

    promise
      .then((dateLocale) => {
        if (dateLocale) {
          setDefaultOptions({ locale: dateLocale });
        }
      })
      .catch((error) => {
        console.error('Failed to update date-fns options', error);
      });
  }, [language]);

  const changeLanguage = useCallback(
    (val: string) => {
      updateLanguage(val);
      queryClient.invalidateQueries();
    },
    [queryClient, updateLanguage]
  );

  const contextValue = useMemo<LangContextProps>(
    () => ({
      language,
      changeLanguage,
      showKeys,
      setShowKeys,
      isReady,
    }),
    [language, changeLanguage, showKeys, setShowKeys, isReady]
  );

  if (texts === undefined) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{ m: 2, width: '50%', height: 50 }}
        delay={2000}
      />
    );
  }

  return (
    <LangContext.Provider value={contextValue}>
      <TranslationProvider
        texts={texts}
        showKeys={showKeys}
        onWarning={handleTranslationWarning}
      >
        {children}
      </TranslationProvider>
    </LangContext.Provider>
  );
};

export default LangProvider;
export { LangContext };

interface Module {
  default: object | undefined;
}

function createTexts(
  { showKeys }: { showKeys: boolean },
  ...translationModules: (Module | undefined)[]
) {
  const translations = translationModules
    .map((m) => m?.default)
    .filter((translation): translation is object => translation !== undefined);

  const texts = mergeTexts(...translations) as AllTexts;

  return showKeys ? withTextKeys(texts) : texts;
}
